import type {
  AmazzingFilterGroup,
  AmazzingFilterValue,
} from "~~/shared/types/store";

export interface FlattenedAmazzingValue extends AmazzingFilterValue {
  depth: number;
  parentId?: string;
  hasChildren: boolean;
}

export interface AmazzingFilterGroupState {
  hasSelection: boolean;
  visibleValues: FlattenedAmazzingValue[];
}

function readAmazzingPosition(value: AmazzingFilterValue): number | null {
  return typeof value.position === "number" && Number.isFinite(value.position)
    ? value.position
    : null;
}

function compareAmazzingPositions(
  left: number | null,
  right: number | null,
  leftIndex: number,
  rightIndex: number,
) {
  if (left === null && right === null) {
    return leftIndex - rightIndex;
  }
  if (left === null) {
    return 1;
  }
  if (right === null) {
    return -1;
  }

  return left - right || leftIndex - rightIndex;
}

function sortAmazzingValues(
  values: AmazzingFilterValue[],
  respectSelected = true,
): AmazzingFilterValue[] {
  const orderedValues = values.map((value) => ({
    ...value,
    children: value.children?.length
      ? sortAmazzingValues(value.children, respectSelected)
      : value.children,
  }));

  return orderedValues
    .map((value, index) => ({
      value,
      index,
      position: readAmazzingPosition(value),
    }))
    .sort((left, right) => {
      if (respectSelected && left.value.selected !== right.value.selected) {
        return Number(right.value.selected) - Number(left.value.selected);
      }
      if (left.value.count !== right.value.count) {
        return right.value.count - left.value.count;
      }
      return compareAmazzingPositions(
        left.position,
        right.position,
        left.index,
        right.index,
      );
    })
    .map((entry) => entry.value);
}

function flattenAmazzingValues(
  values: AmazzingFilterValue[],
  depth = 0,
  flattened: FlattenedAmazzingValue[] = [],
  parentId?: string,
  shouldSort = true,
  respectSelected = true,
) {
  for (const value of shouldSort
    ? sortAmazzingValues(values, respectSelected)
    : values) {
    flattened.push({
      ...value,
      depth,
      parentId,
      hasChildren: (value.children?.length ?? 0) > 0,
    });

    if (value.children?.length) {
      flattenAmazzingValues(
        value.children,
        depth + 1,
        flattened,
        value.id,
        shouldSort,
        respectSelected,
      );
    }
  }

  return flattened;
}

export function buildAmazzingFilterGroupState(
  group: AmazzingFilterGroup,
): AmazzingFilterGroupState {
  const isTree = group.controlType === "tree";
  const flattenedValues = flattenAmazzingValues(
    group.values,
    0,
    [],
    undefined,
    true,
    !isTree,
  );
  const hasSelection = flattenedValues.some((value) => value.selected);

  if (group.controlType === "tree") {
    return {
      hasSelection,
      visibleValues: flattenedValues,
    };
  }

  const visibleIds = new Set<string>();

  for (let index = flattenedValues.length - 1; index >= 0; index -= 1) {
    const value = flattenedValues[index]!;
    const hasVisibleDescendant =
      value.hasChildren &&
      flattenedValues.some(
        (child) => child.parentId === value.id && visibleIds.has(child.id),
      );

    if (value.selected || value.count > 0 || hasVisibleDescendant) {
      visibleIds.add(value.id);
    }
  }

  return {
    hasSelection,
    // Show a value only if it's selected (so user can deselect) OR has matching products.
    // Keep zero-count ancestors when a visible descendant would otherwise become unreachable.
    // Do NOT show zero-count leaves just because some other filter is active.
    visibleValues: flattenedValues.filter((value) => visibleIds.has(value.id)),
  };
}
